const _ = require('lodash');
const axios = require('axios');
const { getContracts, add0x, remove0x, getNetworkId } = require('./utils.js');
const { ContainerType, ByteVectorType } = require("@chainsafe/ssz");

const BLS = require('@chainsafe/bls/herumi');
const constants = require("./constants");

const Registration = new ContainerType({
    fields: {
        blsKey: new ByteVectorType({ length: 48 }),
        domain: new ByteVectorType({ length: 20 }),
    }
});

const _formRegistrationSignature = async (
    blsKey, sigSwapperAddress, blsSigningKey
) => {

    if (!_.isString(blsKey)) {
        throw Error('BLS public key must be provided in string format')
    }

    if (!_.isString(sigSwapperAddress)) {
        throw Error('Builder registry must be provided in string format');
    }

    const registration = Registration.fromJson({
        blsKey: remove0x(blsKey),
        domain: remove0x(sigSwapperAddress)
    });

    const root = Registration.hashTreeRoot(registration);

    await BLS.init();
    const { bls } = BLS;

    return bls.sign(blsSigningKey, root);
}

const _swapSignature = async (signer, signature, blsKey) => {
    const chainId = await getNetworkId(signer);
    const network = constants.chainIdToNetworkmap[chainId];

    if (!network) {
        throw new Error('Invalid network');
    }

    const swapperUrl = constants.BUILDER_SIGNATURE_SWAPPER[network];

    let res;
    try {
        res = await axios.post(swapperUrl, {
            signature,
            blsKey
        });
    }
    catch (error) {
        throw new Error(error.response.data.reason);
    }

    return res.data;
}

const _prepareRegistration = async (signer, blsKey, blsSigningKey) => {
    const networkId = await getNetworkId(signer);
    const networkName = constants.chainIdToNetworkmap[networkId];
    const sigSwapperAddress = constants.CONTRACT_ADDRESSES[networkName].SIGNATURE_SWAPPER;
    const registrationSignature = await _formRegistrationSignature(blsKey, sigSwapperAddress, blsSigningKey)
    const ecdsaSig = await _swapSignature(signer, Buffer.from(registrationSignature).toString('hex'), blsKey);
    return {
        registrationSignature,
        ecdsaSig,
        blsPublicKey: blsKey
    }
}

const _getRegistry = async (signer) => (await getContracts(signer)).builderRegistry;

const _getBuilder = async (signer, address) => {
    const registry = await _getRegistry(signer);

    const [
        designatedSigner,
        balanceStaked,
        status,
        joinBlock,
        exitBlock
    ] = await registry.addressToBuilder(address);

    return {
        signer: designatedSigner,
        balanceStaked: balanceStaked.toString(),
        status,
        joinBlock: joinBlock.toString(),
        exitBlock: exitBlock.toString()
    }
}

const _registerBuilder = async (signer, address, amount) => {
    const registry = await _getRegistry(signer);

    return registry.registerBuilder(address, { value: amount });
}

const _registerBuilderAndAttachBLSCredentialsToBuilder = async (signer, address, amount, blsPublicKey, blsSigningKey) => {
    const registry = await _getRegistry(signer);

    const {
        registrationSignature,
        ecdsaSig
    } = await _prepareRegistration(signer, blsPublicKey, blsSigningKey);

    return registry.registerBuilderAndAttachBLSCredentialsToBuilder(
        address,
        add0x(blsPublicKey),
        add0x(registrationSignature),
        ecdsaSig,
        { value: amount }
    );
}

const _registerBuilderWithBLSCredentialsUsingSignature = async (signer, address, amount, blsPublicKey, registrationSignature, ecdsaSig) => {
    const registry = await _getRegistry(signer);
    return registry.registerBuilderAndAttachBLSCredentialsToBuilder(
        address,
        add0x(blsPublicKey),
        add0x(registrationSignature),
        ecdsaSig,
        { value: amount }
    );
}

const _attachBLSCredentialsToBuilder = async (signer, blsPublicKey, blsSigningKey) => {
    const registry = await _getRegistry(signer);

    const {
        registrationSignature,
        ecdsaSig
    } = await _prepareRegistration(signer, blsPublicKey, blsSigningKey);

    return registry.attachBLSCredentialsToBuilder(
        blsPublicKey,
        registrationSignature,
        ecdsaSig
    );
}

const _positionBuilderForExit = async (signer) => {
    const registry = await _getRegistry(signer);

    return registry.positionBuilderForExit();
}

const _exitBuilder = async (signer) => {
    const registry = await _getRegistry(signer);

    return registry.exitBuilder();
}

const _getMinimalStakeAmount = async (signer) => {
    const registry = await _getRegistry(signer);

    return registry.getMinimumStakeAmount()
}

const _isBuilderRegistered = async (signer, address) => {
    const { status } = await _getBuilder(signer, address);

    return status >= 1;
}

const _getRequiredTopUp = async (signer, builder) => {
    const registry = await _getRegistry(signer);

    return registry.getMinimumTopupToOperational(builder);
}

const _topUpBuilder = async (signer, builder, amount) => {
    const registry = await _getRegistry(signer);

    return registry.topUp(builder, { value: amount });
}

class BuilderRegistry {
    constructor(signer) {
        this.signer = signer;
    }

    generateRegistrationSignature(blsKey, blsSigningKey) {
        return _prepareRegistration(this.signer, blsKey, blsSigningKey);
    }

    registerBuilderWithBLSCredentialsUsingSignature(address, amount, blsPublicKey, registrationSignature, ecdsaSig) {
        return _registerBuilderWithBLSCredentialsUsingSignature(this.signer, address, amount, blsPublicKey, registrationSignature, ecdsaSig);
    }

    getRegistry() {
        return _getRegistry(this.signer);
    }

    getBuilder(address) {
        return _getBuilder(this.signer, address);
    }

    registerBuilder(alternativeSignerECDSAPubKey, stakeAmount) {
        return _registerBuilder(this.signer, alternativeSignerECDSAPubKey, stakeAmount);
    }

    registerBuilderAndAttachBLSCredentialsToBuilder(
        alternativeSignerECDSAPubKey,
        stakeAmount,
        blsPublicKey,
        blsSigningKey
    ) {
        return _registerBuilderAndAttachBLSCredentialsToBuilder(
            this.signer,
            alternativeSignerECDSAPubKey,
            stakeAmount,
            blsPublicKey,
            blsSigningKey
        );
    }

    attachBLSCredentialsToBuilder(blsPublicKey, blsSigningKey) {
        return _attachBLSCredentialsToBuilder(this.signer, blsPublicKey, blsSigningKey);
    }

    positionBuilderForExit() {
        return _positionBuilderForExit(this.signer);
    }

    exitBuilder() {
        return _exitBuilder(this.signer);
    }

    getMinimalStakeAmount() {
        return _getMinimalStakeAmount(this.signer);
    }

    isBuilderRegistered(address) {
        return _isBuilderRegistered(this.signer, address);
    }

    getRequiredTopUp(builder) {
        return _getRequiredTopUp(this.signer, builder);
    }

    topUp(builder, amount) {
        return _topUpBuilder(this.signer, builder, amount);
    }
}

module.exports = BuilderRegistry
